(() => {
  const readConfig = (element, selector) => {
    try { return JSON.parse(element.querySelector(selector)?.textContent || 'null'); }
    catch (error) { console.warn('Invalid Listing configuration', error); return null; }
  };
  const formatMoney = (amount, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style:'currency', currency:currency || 'USD' }).format(amount / 100);
  const imageURL = (product, variant) => {
    const image = variant?.featured_image || product.featured_image || product.images?.[0];
    const url = typeof image === 'string' ? image : image?.src;
    return typeof url === 'string' && /^(https?:\/\/|\/\/|\/[^/])/.test(url) ? url : '';
  };
  const quantityRule = variant => ({ min:variant?.quantity_rule?.min || 1, max:variant?.quantity_rule?.max ?? Infinity, step:variant?.quantity_rule?.increment || 1 });
  const validQuantity = (quantity, variant) => {
    const {min,max,step} = quantityRule(variant);
    return Number.isSafeInteger(quantity) && quantity >= min && quantity <= max && (quantity-min)%step === 0;
  };
  const syncQuantityRule = (input, variant) => {
    if(!input || !variant) return;
    const {min,max,step}=quantityRule(variant);
    input.min=min; input.step=step; input.dataset.min=min;
    if(Number.isFinite(max)) { input.max=max; input.dataset.max=max; }
    else { input.removeAttribute('max'); delete input.dataset.max; }
    const quantity=Number(input.value);
    const normalized=min+Math.floor((Math.max(min,Math.min(max,quantity || min))-min)/step)*step;
    const changed=quantity!==normalized; if(changed) input.value=normalized;
    const selector=input.closest('quantity-selector');
    if(selector) { selector.step=step; selector.validateQtyRules?.(); }
    if(changed) input.dispatchEvent(new Event('change',{bubbles:true}));
  };
  const text = (parent, selector, value) => { const node=parent.querySelector(selector); if(node) node.textContent=value; };
  class ListingPurchaseDetails extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.controller) return;
        this.config=readConfig(this,'[data-purchase-config]');
        if (!this.config?.product) return;
        this.controller=new AbortController();
        this.wrapper=this.closest('[data-listing-product]');
        const variantId=this.closest('form')?.querySelector('[name=id]')?.value || this.config.initialVariant;
        this.variant=this.config.product.variants.find(v=>String(v.id)===String(variantId));
        this.quantity=this.closest('form')?.querySelector('[name=quantity]');
        this.stickyPrice=this.wrapper?.querySelector('.listing-sticky__price');
        // Variant section responses replace the price markup asynchronously.
        this.priceObserver=new MutationObserver(()=>this.updateStickyTotal());
        if(this.stickyPrice) this.priceObserver.observe(this.stickyPrice,{childList:true,subtree:true,characterData:true});
        syncQuantityRule(this.quantity,this.variant);
        this.autoGiftQuantity=this.config.giftRules == null || (typeof this.config.giftRules==='string' && !this.config.giftRules.trim());
        try { this.rules=typeof this.config.giftRules==='string' ? JSON.parse(this.config.giftRules) : this.config.giftRules; } catch { this.rules={}; }
        const options={signal:this.controller.signal};
        this.quantity?.addEventListener('input',()=>this.update(),options);
        this.quantity?.addEventListener('change',()=>this.update(),options);
        document.addEventListener('product:variant-change',event=>{
          if(event.detail?.sectionId!==this.dataset.section) return;
          this.variant=event.detail.variant;
          syncQuantityRule(this.quantity,this.variant);
          this.update();
        },options);
        this.automaticGifts=[];
        this.giftKey=null;
        if(this.config.automaticGift && window.ieGeekListingGifts) {
          this.unsubscribeGifts=window.ieGeekListingGifts.subscribe(data=>{ this.kiteData=data; this.update(); });
        }
        this.update();
      });
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller=null; this.priceObserver?.disconnect(); this.unsubscribeGifts?.();
    }
    updateStickyTotal() {
      const price=this.stickyPrice?.querySelector('.price'), amount=price?.querySelector('ins .amount');
      if(!amount) return;
      const quantity=Number(this.quantity?.value), variant=this.variant;
      const valid=!!variant && validQuantity(quantity,variant);
      const discounted=valid && variant.compare_at_price>variant.price;
      const money=value=>formatMoney(value,this.dataset.currency)+(this.dataset.currencyCodeEnabled==='true' ? ' '+this.dataset.currency : '');
      // Only write changed text so observing a section refresh cannot cause a loop.
      const setText=(node,value)=>{if(node.textContent!==value) node.textContent=value;};
      setText(amount,valid ? money(variant.price*quantity) : '—');
      amount.classList.toggle('discounted',!!discounted);
      let compare=price.querySelector('del');
      if(discounted && !compare) {
        compare=document.createElement('del');
        const value=document.createElement('span'); value.className='amount'; compare.append(value);
        amount.closest('ins').after(compare);
      }
      if(compare) {
        compare.hidden=!discounted;
        if(discounted) setText(compare.querySelector('.amount'),money(variant.compare_at_price*quantity));
      }
      let badge=price.querySelector('.badge.onsale');
      if(discounted && !badge) {
        let badges=price.querySelector('.badges');
        if(!badges) { badges=document.createElement('span'); badges.className='badges'; price.append(badges); }
        badge=document.createElement('span'); badge.className='badge onsale'; badges.prepend(badge);
      }
      if(badge) {
        badge.hidden=!discounted;
        if(discounted) setText(badge,Math.round((variant.compare_at_price-variant.price)*100/variant.compare_at_price)+'% OFF');
      }
    }
    giftCount(variant) {
      if(!this.config.gift || !variant?.available) return 0;
      // Only explicit free SD card wording implies a gift; camera pack size does not.
      // A configured map remains authoritative, including zero or missing entries.
      const count=Number(this.autoGiftQuantity ? variant.title?.match(/\b(\d+)\s*free\s*sd\s*cards?\b/i)?.[1] : this.rules?.[variant.id]);
      return Number.isSafeInteger(count) && count>0 ? count : 0;
    }
    updateAutomaticGifts() {
      const helper=window.ieGeekListingGifts;
      if(!this.config.automaticGift || !helper) return;
      const descriptors=this.variant?.available ? helper.resolve(this.config.product,this.variant.id,this.kiteData,{
        quantity:Number(this.quantity?.value),currency:this.dataset.currency,shopCurrency:this.config.shopCurrency
      }) : [];
      const key=JSON.stringify([this.variant?.id,descriptors]);
      if(key===this.giftKey) return;
      this.giftKey=key;
      this.automaticGifts=[];
      const controller=this.controller;
      Promise.all(descriptors.map(gift=>helper.load({...gift,label:this.config.giftLabel || gift.label}))).then(gifts=>{
        if(!this.isConnected || this.controller!==controller || controller.signal.aborted || this.giftKey!==key) return;
        this.automaticGifts=gifts.filter(Boolean).map(gift=>({...gift,count:1}));
        this.update();
      });
    }
    renderGift(gift) {
      const row=document.createElement('div'); row.className='listing-gift__row';
      const image=document.createElement('div'); image.className='listing-gift__image';
      if(gift.image) {
        const img=document.createElement('img'); img.width=80; img.height=80; img.loading='lazy'; img.alt=gift.title; img.src=gift.image;
        image.append(img);
      }
      const copy=document.createElement('div'); copy.className='listing-gift__copy';
      const heading=document.createElement('div'); heading.className='listing-gift__heading';
      const title=document.createElement('span'); title.dataset.giftTitle=''; title.textContent=gift.title+(gift.count>1 ? ' × '+gift.count : '');
      const pricing=document.createElement('span'); pricing.className='listing-gift__pricing';
      const price=document.createElement('strong'); price.textContent=formatMoney(0,this.dataset.currency);
      const compare=document.createElement('del'); compare.dataset.giftCompare=''; compare.textContent=formatMoney(gift.price*gift.count,this.dataset.currency);
      const badge=document.createElement('span'); badge.className='listing-gift__badge'; badge.textContent=gift.label;
      heading.append(title,badge); pricing.append(price,compare); copy.append(heading,pricing); row.append(image,copy);
      if(gift.minimumSubtotal) {
        const condition=document.createElement('span'); condition.className='listing-gift__condition';
        condition.textContent='On orders of '+formatMoney(gift.minimumSubtotal,this.dataset.currency)+'+ after discounts';
        copy.append(condition);
      }
      return row;
    }
    update() {
      const quantity=Number(this.quantity?.value), variant=this.variant;
      const valid=!!variant && validQuantity(quantity,variant), giftCount=this.giftCount(variant);
      this.updateAutomaticGifts();
      const gifts=this.config.automaticGift ? this.automaticGifts : giftCount ? [{...this.config.gift,count:giftCount}] : [];
      const giftField=[...this.wrapper.querySelectorAll('fieldset[data-listing-option-index]')].filter(f=>!f.classList.contains('listing-option--color')).at(-1);
      this.updateStickyTotal();
      this.wrapper?.querySelectorAll('[data-listing-gift-slot]').forEach(slot=>{
        const field=slot.closest('fieldset'), input=slot.parentElement.querySelector('input');
        const selected=giftField ? input?.checked && field===giftField : slot.hasAttribute('data-listing-gift-fallback');
        const show=valid && variant?.available && gifts?.length>0 && selected;
        slot.hidden=!show;
        if(!show) return;
        const key=JSON.stringify(gifts);
        if(slot.dataset.giftContent!==key) {
          slot.replaceChildren(...gifts.map(gift=>this.renderGift(gift)));
          slot.dataset.giftContent=key;
        }
      });
    }
  }
  if(!customElements.get('listing-purchase-details')) customElements.define('listing-purchase-details',ListingPurchaseDetails);

  class ListingBundle extends HTMLElement {
    connectedCallback() {
      queueMicrotask(()=>{
        if(!this.isConnected || this.controller) return;
        this.config=readConfig(this,'[data-bundle-config]');
        if(!this.config?.main?.variants?.length || !this.config.categories?.length) return;
        this.controller=new AbortController();
        this.form=document.getElementById(this.dataset.formId);
        this.wrapper=this.closest('[data-listing-product]');
        this.dialog=this.querySelector('dialog');
        this.selected=new Map(); this.cards=[]; this.busy=false;
        this.main=this.config.main.variants.find(v=>String(v.id)===this.form?.querySelector('[name=id]')?.value) || this.config.main.variants[0];
        this.mainValid=true;
        this.renderCatalog();
        const options={signal:this.controller.signal};
        this.addEventListener('click',event=>this.onClick(event),options);
        this.addEventListener('change',event=>this.onChange(event),options);
        this.addEventListener('keydown',event=>this.onKeydown(event),options);
        this.form?.querySelector('[name=quantity]')?.addEventListener('change',()=>this.render(),options);
        this.form?.querySelector('[name=quantity]')?.addEventListener('input',()=>this.render(),options);
        document.addEventListener('product:variant-change',event=>{
          if(event.detail?.sectionId!==this.dataset.section) return;
          this.mainValid=!!event.detail.variant;
          if(event.detail.variant) this.main=event.detail.variant;
          this.render();
        },options);
        this.dialog.addEventListener('close',()=>{
          document.documentElement.classList.remove('listing-bundle-open');
          this.querySelector('[data-bundle-open]')?.focus({preventScroll:true});
        },options);
        this.dialog.addEventListener('cancel',event=>{
          if(event.defaultPrevented) return;
          event.preventDefault();
          this.requestClose();
        },options);
        this.render();
      });
    }
    disconnectedCallback() {
      this.dialog?.close();
      this.controller?.abort(); this.controller=null;
      document.documentElement.classList.remove('listing-bundle-open');
    }
    price(value) { return formatMoney(value,this.dataset.currency); }
    qty() { return Number(this.form?.querySelector('[name=quantity]')?.value || quantityRule(this.main).min); }
    fillSelect(select,product,variant) {
      select.replaceChildren(...product.variants.map(v=>{
        const option=new Option(v.title+(v.available ? '' : ' — Sold out'),String(v.id));
        option.disabled=!v.available || !!v.requires_selling_plan;
        option.selected=String(v.id)===String(variant.id); return option;
      }));
      select.setAttribute('aria-label',product.title+' variant');
    }
    renderCatalog() {
      this.config.categories.forEach(category=>{
        const panel=this.querySelector('[data-bundle-panel="'+category.key+'"]');
        if(!panel) return;
        panel.replaceChildren();
        (category.products || []).filter(p=>String(p.id)!==String(this.config.main.id)).forEach(product=>{
          if(!Array.isArray(product.variants) || !product.variants.length) return;
          const variant=product.variants.find(v=>v.available && !v.requires_selling_plan) || product.variants[0];
          const card=this.querySelector('[data-bundle-card-template]').content.firstElementChild.cloneNode(true);
          card.dataset.bundleKey='card-'+this.cards.length;
          this.fillSelect(card.querySelector('[data-bundle-variant]'),product,variant);
          text(card,'[data-bundle-title]',product.title);
          card.querySelector('[data-bundle-title]').title=product.title;
          const state={card,product,variant}; this.cards.push(state); panel.append(card);
        });
      });
    }
    fillRow(row,product,variant,quantity,included,main=false) {
      const img=row.querySelector('[data-bundle-image]'), url=imageURL(product,variant);
      img.hidden=!url; if(url && img.getAttribute('src')!==url) img.src=url; img.alt=product.title;
      text(row,'[data-bundle-title]',product.title);
      text(row,'[data-bundle-price]',this.price(variant.price));
      text(row,'[data-bundle-quantity]',quantity || '');
      const compare=row.querySelector('[data-bundle-compare]');
      if(compare) { compare.hidden=!(variant.compare_at_price>variant.price); compare.textContent=this.price(variant.compare_at_price || 0); }
      const minus=row.querySelector('[data-bundle-minus]'), plus=row.querySelector('[data-bundle-plus]');
      const rule=quantityRule(variant);
      minus.hidden=!main && !quantity;
      minus.disabled=this.busy || (main && quantity<=rule.min);
      const minusIcon=minus.querySelector('img');
      if(minusIcon) minusIcon.src=minusIcon.getAttribute('src').replace(/minus(?:-disabled)?\.svg/,minus.disabled ? 'minus-disabled.svg' : 'minus.svg');
      plus.disabled=this.busy || !variant.available || variant.requires_selling_plan || quantity+rule.step>rule.max;
      if(main && !this.mainValid) plus.disabled=true;
      const checked=row.querySelector('[data-bundle-checked]');
      if(checked) { checked.checked=included; checked.disabled=this.busy; checked.setAttribute('aria-label','Include '+product.title); }
      row.querySelector('[data-bundle-variant]').disabled=this.busy;
    }
    render() {
      const focus=document.activeElement?.closest('listing-select')?.querySelector('select') || document.activeElement;
      const key=focus?.closest('[data-bundle-key]')?.dataset.bundleKey;
      const focusAttribute=['data-bundle-plus','data-bundle-minus','data-bundle-checked','data-bundle-variant'].find(a=>focus?.hasAttribute(a));
      const quantity=this.qty(), valid=this.mainValid && this.main.available && validQuantity(quantity,this.main);
      this.cards.forEach(({card,product,variant})=>{
        const selected=this.selected.get(String(variant.id));
        this.fillRow(card,product,variant,selected?.included ? selected.quantity : 0,true);
      });
      const items=this.querySelector('[data-bundle-items]'); items.replaceChildren();
      const addRow=(product,variant,count,included,key,main)=>{
        const row=this.querySelector('[data-bundle-item-template]').content.firstElementChild.cloneNode(true);
        row.dataset.bundleKey=key; row.classList.toggle('listing-bundle__item--main',!!main);
        this.fillSelect(row.querySelector('[data-bundle-variant]'),product,variant);
        if(!main) { const select=row.querySelector('[data-bundle-variant]'); select.closest('.listing-bundle__select-wrap').hidden=true; const name=document.createElement('span'); name.className='listing-bundle__item-variant-name'; name.textContent=variant.title; select.closest('.listing-bundle__select-wrap').after(name); }
        this.fillRow(row,product,variant,count,included,main); items.append(row);
      };
      addRow(this.config.main,this.main,quantity,true,'main',true);
      let total=valid ? this.main.price*quantity : 0;
      let compare=valid ? Math.max(this.main.price,this.main.compare_at_price || 0)*quantity : 0;
      let count=valid ? quantity : 0;
      this.selected.forEach((state,id)=>{
        addRow(state.product,state.variant,state.quantity,state.included,id,false);
        if(state.included) { total+=state.variant.price*state.quantity; compare+=Math.max(state.variant.price,state.variant.compare_at_price || 0)*state.quantity; count+=state.quantity; }
      });
      text(this,'[data-bundle-total]',valid ? this.price(total) : '—');
      text(this,'[data-bundle-count]',count);
      const compareNode=this.querySelector('.listing-bundle__footer [data-bundle-compare]');
      compareNode.hidden=!valid || compare<=total; compareNode.textContent=this.price(compare);
      const hasAddon=[...this.selected.values()].some(s=>s.included && s.quantity>0);
      this.querySelector('[data-bundle-add]').disabled=this.busy || !valid || !hasAddon;
      if(key && focusAttribute) this.querySelector('[data-bundle-key="'+key+'"] ['+focusAttribute+']')?.focus({preventScroll:true});
    }
    activateCategory(key,focus=false) {
      this.querySelectorAll('[data-bundle-category]').forEach(tab=>{
        const active=tab.dataset.bundleCategory===key;
        tab.classList.toggle('is-active',active); tab.setAttribute('aria-selected',String(active)); tab.tabIndex=active ? 0 : -1;
        if(active && focus) { tab.focus(); tab.scrollIntoView({block:'nearest',inline:'nearest'}); }
      });
      this.querySelectorAll('[data-bundle-panel]').forEach(panel=>{panel.hidden=panel.dataset.bundlePanel!==key;});
      this.querySelector('[data-bundle-catalog]').scrollTop=0;
    }
    onKeydown(event) {
      const tab=event.target.closest('[data-bundle-category]');
      if(!tab || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
      event.preventDefault(); const tabs=[...this.querySelectorAll('[data-bundle-category]')]; const i=tabs.indexOf(tab);
      const next=event.key==='Home' ? 0 : event.key==='End' ? tabs.length-1 : (i+(event.key==='ArrowRight' ? 1 : -1)+tabs.length)%tabs.length;
      this.activateCategory(tabs[next].dataset.bundleCategory,true);
    }
    setView(view,resetScroll=true) {
      this.dialog.dataset.view=view;
      if(resetScroll) this.querySelector(view==='summary' ? '[data-bundle-summary]' : '[data-bundle-catalog]').scrollTop=0;
      text(this,'[data-bundle-heading]',view==='summary' ? this.querySelector('[data-bundle-summary] h3').textContent : this.heading);
      this.querySelector('[data-bundle-summary-toggle]').setAttribute('aria-label',view==='summary' ? 'Back to bundle products' : 'View bundle summary');
      this.querySelector('[data-bundle-close]').setAttribute('aria-label',view==='summary' ? 'Close bundle summary' : 'Close bundle builder');
    }
    requestClose() {
      if(this.dialog.dataset.view==='summary' && window.matchMedia('(max-width: 767px)').matches) {
        this.setView('catalog',false);
        this.querySelector('[data-bundle-summary-toggle]').focus({preventScroll:true});
      } else this.dialog.close();
    }
    onClick(event) {
      if(event.target===this.dialog) {
        const r=this.dialog.getBoundingClientRect();
        if(event.clientX<r.left || event.clientX>r.right || event.clientY<r.top || event.clientY>r.bottom) { this.requestClose(); return; }
      }
      if(event.target.closest('[data-bundle-open]')) {
        if(!this.form?.reportValidity()) return;
        this.heading=this.heading || this.querySelector('[data-bundle-heading]').textContent;
        this.setView('catalog'); this.activateCategory(this.config.categories[0].key); this.render();
        document.documentElement.classList.add('listing-bundle-open'); this.dialog.showModal();
        this.querySelectorAll('[data-bundle-catalog],[data-bundle-summary]').forEach(pane=>{pane.scrollTop=0;});
      }
      if(event.target.closest('[data-bundle-close]')) this.requestClose();
      if(event.target.closest('[data-bundle-summary-toggle]')) this.setView(this.dialog.dataset.view==='summary' ? 'catalog' : 'summary');
      const tab=event.target.closest('[data-bundle-category]');
      if(tab) this.activateCategory(tab.dataset.bundleCategory);
      const control=event.target.closest('[data-bundle-plus],[data-bundle-minus]');
      if(control && !control.disabled && !this.busy) {
        const row=control.closest('[data-bundle-key]'), key=row.dataset.bundleKey;
        const increase=control.hasAttribute('data-bundle-plus');
        if(key==='main') {
          const input=this.form.querySelector('[name=quantity]'), rule=quantityRule(this.main);
          input.value=Math.max(rule.min,this.qty()+(increase ? rule.step : -rule.step)); input.dispatchEvent(new Event('change',{bubbles:true}));
        } else {
          const card=this.cards.find(c=>c.card===row);
          const product=card?.product || this.selected.get(key)?.product, variant=card?.variant || this.selected.get(key)?.variant;
          if(!variant) return;
          const id=String(variant.id), old=this.selected.get(id), rule=quantityRule(variant);
          const qty=old?.included ? old.quantity : 0;
          const next=increase ? (qty ? qty+rule.step : rule.min) : qty-rule.step;
          if(next<rule.min) this.selected.delete(id);
          else if(validQuantity(next,variant)) this.selected.set(id,{product,variant,quantity:next,included:true});
          this.render();
        }
      }
      if(event.target.closest('[data-bundle-add]') && !this.busy) this.addToCart();
    }
    onChange(event) {
      const checked=event.target.closest('[data-bundle-checked]');
      if(checked && !this.busy) {
        const key=checked.closest('[data-bundle-key]').dataset.bundleKey;
        if(!checked.checked) this.selected.delete(key);
        else { const state=this.selected.get(key); if(state) state.included=true; }
        this.render();
        if(!checked.checked) {
          const next=this.querySelector('[data-bundle-items] [data-bundle-checked]') || this.querySelector('[data-bundle-key="main"] [data-bundle-plus]');
          next?.focus({preventScroll:true});
        }
      }
      const select=event.target.closest('[data-bundle-variant]');
      if(!select || this.busy) return;
      const row=select.closest('[data-bundle-key]');
      if(row.dataset.bundleKey==='main') {
        const variant=this.config.main.variants.find(v=>String(v.id)===select.value);
        if(!variant?.available) return;
        const picker=this.wrapper.querySelector('variant-selects');
        const fields=[...picker?.querySelectorAll('fieldset') || []];
        let last;
        fields.forEach((field,i)=>{const input=[...field.querySelectorAll('input')].find(x=>x.value===variant.options[i]); if(input) { input.checked=true; last=input; }});
        if(last) last.dispatchEvent(new Event('change',{bubbles:true}));
        else { this.main=variant; this.render(); }
      } else {
        const card=this.cards.find(c=>c.card===row);
        if(card) { card.variant=card.product.variants.find(v=>String(v.id)===select.value) || card.variant; this.render(); }
      }
    }
    async addToCart() {
      const button=this.querySelector('[data-bundle-add]');
      if(button.disabled || !this.form?.reportValidity()) return;
      const mainInput=this.form.querySelector('[name=id]');
      if(!this.mainValid || String(this.main.id)!==mainInput?.value) return;
      const addonItems=[...this.selected.values()].filter(s=>s.included);
      if(!addonItems.length || !addonItems.every(s=>s.variant.available && !s.variant.requires_selling_plan && validQuantity(s.quantity,s.variant))) return;
      const formData=new FormData(this.form), properties={};
      const error=this.querySelector('[data-bundle-error]'); error.hidden=true;
      for(const [name,value] of formData) {
        const match=name.match(/^properties\[(.*)\]$/);
        if(match) {
          if(value instanceof File && value.size) { error.textContent='Please add a product with a file attachment using its product form.'; error.hidden=false; return; }
          if(typeof value==='string') properties[match[1]]=value;
        }
      }
      const mainItem={id:this.main.id,quantity:this.qty()};
      if(Object.keys(properties).length) mainItem.properties=properties;
      const sellingPlan=formData.get('selling_plan'); if(sellingPlan) mainItem.selling_plan=sellingPlan;
      const items=[mainItem,...addonItems.map(s=>({id:s.variant.id,quantity:s.quantity}))];
      this.busy=true; this.render(); this.setAttribute('aria-busy','true');
      const drawer=document.getElementById('Cart-Drawer'); let mutationFinished=false, added=false;
      drawer?.beginMutation?.();
      try {
        const response=await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json','X-Requested-With':'XMLHttpRequest'},body:JSON.stringify({items})});
        const result=await response.json();
        if(!response.ok || result.status) throw new Error(result.description || result.message || 'Could not add the bundle. Please try again.');
        added=true; drawer?.endMutation?.(); mutationFinished=true;
        if(!this.isConnected) { document.documentElement.dispatchEvent(new CustomEvent('cart:refresh',{bubbles:true})); return; }
        this.dialog.close();
        if(typeof drawer?.queueRefresh==='function') {
          const refreshed=await drawer.queueRefresh({open:true});
          if(refreshed) drawer.open?.();
          else window.location.assign(window.theme?.routes?.cart_url || '/cart');
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh',{bubbles:true}));
          if(drawer?.open) drawer.open(); else window.location.assign(window.theme?.routes?.cart_url || '/cart');
        }
        document.documentElement.dispatchEvent(new CustomEvent('cart:item-added',{bubbles:true,detail:{product:result.items?.[0] || result}}));
      } catch (failure) {
        if(added) { window.location.assign(window.theme?.routes?.cart_url || '/cart'); }
        else if(this.isConnected) { error.textContent=failure.message; error.hidden=false; }
      } finally {
        if(!mutationFinished) drawer?.endMutation?.();
        this.busy=false; this.removeAttribute('aria-busy'); if(this.isConnected) this.render();
      }
    }
  }
  if(!customElements.get('listing-bundle')) customElements.define('listing-bundle',ListingBundle);
})();
